/**
 * GraphQL Admin AI Personas Hooks
 *
 * Hooks for admin AI persona management operations.
 * Feature: 071-ai-critique-sessions (US2)
 */

import { useQuery, useMutation, gql } from '@apollo/client';
import logger from '../../utils/logger';

// GraphQL Queries
const ADMIN_AI_PERSONAS_QUERY = gql`
  query AdminAiPersonas {
    adminAiPersonas
  }
`;

const ADMIN_AI_PERSONA_QUERY = gql`
  query AdminAiPersona($id: String!) {
    adminAiPersona(id: $id)
  }
`;

// GraphQL Mutations
const ADMIN_CREATE_AI_PERSONA_MUTATION = gql`
  mutation AdminCreateAiPersona($input: AdminCreateAIPersonaInput!) {
    adminCreateAiPersona(input: $input)
  }
`;

const ADMIN_UPDATE_AI_PERSONA_MUTATION = gql`
  mutation AdminUpdateAiPersona($id: String!, $input: AdminUpdateAIPersonaInput!) {
    adminUpdateAiPersona(id: $id, input: $input)
  }
`;

const ADMIN_DELETE_AI_PERSONA_MUTATION = gql`
  mutation AdminDeleteAiPersona($id: String!) {
    adminDeleteAiPersona(id: $id)
  }
`;

const ADMIN_IMPORT_AI_PERSONA_MUTATION = gql`
  mutation AdminImportAiPersona($markdown: String!) {
    adminImportAiPersona(markdown: $markdown)
  }
`;

// Types
// Note: Admin personas use aiProvider/aiModel/aiPrompt prefix to distinguish admin-only fields.
// Public AvailableAIPersona (in useAIPersonas.ts) uses shorter provider/model names.
export interface AIPersona {
  id: string;
  globalId: string;
  email: string;
  username: string;
  slug: string;
  displayName: string;
  isAiUser: boolean;
  aiProvider: 'openai' | 'anthropic' | 'google';
  aiModel: string;
  aiPrompt: string;
  createdAt: string;
  updatedAt: string;
}

export interface AIPersonasResponse {
  personas: AIPersona[];
  total: number;
}

export interface CreateAIPersonaInput {
  username: string;
  displayName: string;
  provider: string;
  model: string;
  prompt: string;
}

export interface UpdateAIPersonaInput {
  username?: string;
  displayName?: string;
  provider?: string;
  model?: string;
  prompt?: string;
}

interface SuccessPayload {
  success: boolean;
  message: string;
}

export interface ImportResult extends AIPersona {
  wasUpdated: boolean;
}

/**
 * Hook: Fetch all AI personas
 */
export function useAdminAIPersonasGraphQL() {
  const { data, loading, error, refetch } = useQuery<{ adminAiPersonas: AIPersonasResponse }>(
    ADMIN_AI_PERSONAS_QUERY,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onError: (err) => {
        logger.error('Admin', 'Failed to fetch AI personas', err);
      },
    }
  );

  return {
    data: data?.adminAiPersonas,
    loading,
    error,
    refetch,
    isLoading: loading,
    isError: !!error,
  };
}

/**
 * Hook: Fetch single AI persona by ID
 * Automatically fetches when personaId is provided and changes.
 */
export function useAdminAIPersonaGraphQL(personaId?: string) {
  const { data, loading, error, refetch } = useQuery<
    { adminAiPersona: AIPersona | null },
    { id: string }
  >(ADMIN_AI_PERSONA_QUERY, {
    variables: { id: personaId || '' },
    skip: !personaId,
    fetchPolicy: 'network-only',
    onError: (err) => {
      logger.error('Admin', 'Failed to fetch AI persona', err);
    },
  });

  return {
    data: data?.adminAiPersona,
    loading,
    error,
    refetch,
    isLoading: loading,
    isError: !!error,
  };
}

/**
 * Hook: Create AI persona
 */
export function useAdminCreateAIPersonaGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { adminCreateAiPersona: AIPersona },
    { input: CreateAIPersonaInput }
  >(ADMIN_CREATE_AI_PERSONA_MUTATION, {
    onCompleted: (data) => {
      logger.info('Admin', 'Created AI persona via GraphQL', {
        id: data.adminCreateAiPersona.id,
        username: data.adminCreateAiPersona.username,
      });
    },
    onError: (err) => {
      logger.error('Admin', 'Failed to create AI persona', err);
    },
  });

  const createPersona = async (input: CreateAIPersonaInput): Promise<AIPersona> => {
    const result = await mutate({ variables: { input } });
    // Apollo throws on GraphQL/network errors via onError, so null result is unexpected
    if (!result.data?.adminCreateAiPersona) {
      throw new Error('Failed to create AI persona - unexpected empty response');
    }
    return result.data.adminCreateAiPersona;
  };

  return { createPersona, loading, error };
}

/**
 * Hook: Update AI persona
 */
export function useAdminUpdateAIPersonaGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { adminUpdateAiPersona: AIPersona | null },
    { id: string; input: UpdateAIPersonaInput }
  >(ADMIN_UPDATE_AI_PERSONA_MUTATION, {
    onCompleted: (data) => {
      if (data.adminUpdateAiPersona) {
        logger.info('Admin', 'Updated AI persona via GraphQL', {
          id: data.adminUpdateAiPersona.id,
        });
      }
    },
    onError: (err) => {
      logger.error('Admin', 'Failed to update AI persona', err);
    },
  });

  const updatePersona = async (personaId: string, input: UpdateAIPersonaInput): Promise<AIPersona> => {
    const result = await mutate({ variables: { id: personaId, input } });
    // Apollo throws on GraphQL/network errors via onError, so null result means not found
    if (!result.data?.adminUpdateAiPersona) {
      throw new Error(`AI persona ${personaId} not found or update failed`);
    }
    return result.data.adminUpdateAiPersona;
  };

  return { updatePersona, loading, error };
}

/**
 * Hook: Delete AI persona
 */
export function useAdminDeleteAIPersonaGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { adminDeleteAiPersona: SuccessPayload },
    { id: string }
  >(ADMIN_DELETE_AI_PERSONA_MUTATION, {
    onCompleted: () => {
      logger.info('Admin', 'Deleted AI persona via GraphQL');
    },
    onError: (err) => {
      logger.error('Admin', 'Failed to delete AI persona', err);
    },
  });

  const deletePersona = async (personaId: string): Promise<void> => {
    await mutate({ variables: { id: personaId } });
  };

  return { deletePersona, loading, error };
}

/**
 * Hook: Import AI persona from markdown
 */
export function useAdminImportAIPersonaGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { adminImportAiPersona: ImportResult },
    { markdown: string }
  >(ADMIN_IMPORT_AI_PERSONA_MUTATION, {
    onCompleted: (data) => {
      const wasUpdated = data.adminImportAiPersona.wasUpdated;
      logger.info('Admin', wasUpdated ? 'Updated AI persona via import' : 'Created AI persona via import', {
        username: data.adminImportAiPersona.username,
      });
    },
    onError: (err) => {
      logger.error('Admin', 'Failed to import AI persona', err);
    },
  });

  const importPersona = async (markdown: string): Promise<ImportResult> => {
    const result = await mutate({ variables: { markdown } });
    // Apollo throws on GraphQL/network errors via onError, so null result is unexpected
    if (!result.data?.adminImportAiPersona) {
      throw new Error('Failed to import AI persona - unexpected empty response');
    }
    return result.data.adminImportAiPersona;
  };

  return { importPersona, loading, error };
}

/**
 * Valid AI providers and their models
 * Updated November 2025 with current API-available models
 */
export const AI_PROVIDERS = ['openai', 'anthropic', 'google'] as const;

export const AI_MODELS: Record<typeof AI_PROVIDERS[number], string[]> = {
  openai: [
    'gpt-4.1',           // Latest flagship (April 2025)
    'gpt-4.1-mini',      // Cost-efficient 4.1 variant
    'gpt-4o',            // Previous generation flagship
    'gpt-4o-mini',       // Previous generation mini
  ],
  anthropic: [
    'claude-opus-4-5-20251101',    // Most capable model
    'claude-sonnet-4-5-20250929',  // Best coding model
    'claude-haiku-4-5-20251001',   // Fast and cost-effective
    'claude-sonnet-4-20250514',    // Previous generation
  ],
  google: [
    'gemini-2.5-flash',  // Latest stable flash
    'gemini-2.5-pro',    // Latest pro model
    'gemini-2.0-flash',  // Previous generation flash
    'gemini-1.5-pro',    // Legacy pro (still available)
  ],
};

/**
 * Get display name for provider
 */
export function getProviderDisplayName(provider: string): string {
  const names: Record<string, string> = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    google: 'Google',
  };
  return names[provider] || provider;
}
